import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PaymentMethodModal, { Gateway, PaymentMethod } from './PaymentMethodModal';
import { formatDate as formatIntlDate } from '../utils/date';
import { API_URL } from '../api/base';

interface Event {
    id: number;
    slug: string;
    name: { ru: string; ky: string; en?: string };
    eventDate: string;
    locationName: string;
    status: string;
}

interface Participant {
    fullName: string;
}

export type TicketFormProps = { variant?: 'page' | 'hero' | 'inline' };

const TicketForm: React.FC<TicketFormProps> = ({ variant = 'page' }) => {
    const { t, i18n } = useTranslation();

    const [events, setEvents] = useState<Event[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [instagramUrl, setInstagramUrl] = useState('');
    const [additionalParticipants, setAdditionalParticipants] = useState<Participant[]>([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Состояние модалки
    const [modalOpen, setModalOpen] = useState(false);
    const [pendingPayload, setPendingPayload] = useState<any | null>(null);

    const API_BASE = API_URL;

    // Загрузка списка событий
    useEffect(() => {
        const loadEvents = async () => {
            try {
                const response = await fetch(`${API_BASE}/api/events?status=active`);
                if (response.ok) {
                    const data = await response.json();
                    const activeEvents: Event[] = (data.events || []).filter((e: Event) =>
                        e.status === 'active' || e.status === 'planned'
                    );
                    setEvents(activeEvents);

                    // Автоматически выбираем первое событие
                    if (activeEvents.length > 0 && !selectedEventId) {
                        setSelectedEventId(activeEvents[0].id);
                        setSelectedEvent(activeEvents[0]);
                    }
                }
            } catch (error) {
                console.error('Error loading events:', error);
            }
        };

        loadEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [API_BASE]);

    // Обновляем выбранное событие при изменении ID
    useEffect(() => {
        if (selectedEventId) {
            const event = events.find(e => e.id === selectedEventId);
            setSelectedEvent(event || null);
        }
    }, [selectedEventId, events]);

    // Валидация email
    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
    };

    // Обработка телефона
    const handlePhoneChange = (v: string) => {
        let s = v.replace(/[^\d+]/g, '');
        if (!s.startsWith('+')) s = '+' + s.replace(/\+/g, '');
        s = '+' + s.slice(1).replace(/\+/g, '').replace(/[^\d]/g, '').slice(0, 15);
        setPhone(s);
    };

    // Обработка Instagram URL
    const handleInstagramChange = (value: string) => {
        if (!value.trim()) {
            setInstagramUrl('');
            return;
        }

        let cleanValue = value.trim();

        if (cleanValue.includes('instagram.com')) {
            let url = cleanValue;
            if (!url.startsWith('http')) {
                url = 'https://' + url;
            }
            url = url.replace('instagram.com/@', 'instagram.com/');
            const match = url.match(/instagram\.com\/([a-zA-Z0-9_.]+)/);
            if (match && match[1]) {
                setInstagramUrl(`https://www.instagram.com/${match[1]}`);
            } else {
                setInstagramUrl(cleanValue);
            }
        } else if (cleanValue.startsWith('@')) {
            const username = cleanValue.slice(1);
            setInstagramUrl(`https://www.instagram.com/${username}`);
        } else {
            setInstagramUrl(`https://www.instagram.com/${cleanValue}`);
        }
    };

    // Добавить участника
    const addParticipant = () => {
        setAdditionalParticipants([...additionalParticipants, { fullName: '' }]);
    };

    // Удалить участника
    const removeParticipant = (index: number) => {
        setAdditionalParticipants(additionalParticipants.filter((_, i) => i !== index));
    };

    // Изменить данные участника
    const updateParticipant = (index: number, fullName: string) => {
        const updated = [...additionalParticipants];
        updated[index] = { fullName };
        setAdditionalParticipants(updated);
    };

    // Расчет стоимости
    const TREES_PER_TICKET = 12;
    const PRICE_PER_TREE = 249;
    const participantsCount = 1 + additionalParticipants.filter(p => p.fullName.trim()).length;
    const totalTrees = participantsCount * TREES_PER_TICKET;
    const amount = totalTrees * PRICE_PER_TREE;

    const phoneDigits = phone.replace(/\D/g, '');
    const phoneOk = phone.startsWith('+') && phoneDigits.length >= 6 && phoneDigits.length <= 15;
    const emailOk = email.trim() && isValidEmail(email);

    const formInvalid = !selectedEventId || !firstName.trim() || !lastName.trim() || !emailOk || !phoneOk;

    const formatEventDate = (dateString: string) =>
        formatIntlDate(dateString.replace('Z', ''), i18n.language, {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });

    // Получить название события
    const getEventName = (event: Event): string => {
        const lang = i18n.language as keyof typeof event.name;
        return event.name[lang] || event.name.ru || event.slug;
    };

    // Сабмит формы
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (formInvalid || loading || !selectedEvent) return;

        setError(null);
        setLoading(true);

        try {
            // Создаем заказ билета
            const response = await fetch(`${API_BASE}/api/tickets/checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    eventId: selectedEventId,
                    firstName,
                    lastName,
                    email,
                    phone,
                    instagramUrl: instagramUrl || null,
                    additionalParticipants: additionalParticipants
                        .filter(p => p.fullName.trim())
                        .map(p => ({ fullName: p.fullName.trim() }))
                })
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();

            // Сохраняем данные для оплаты
            setPendingPayload(data);
            setModalOpen(true);
        } catch (err) {
            setError(t('tickets.error'));
            console.error('Error creating ticket order:', err);
        } finally {
            setLoading(false);
        }
    };

    // Выбор шлюза в модалке
    const handleConfirmGateway = async (gateway: Gateway, method: PaymentMethod) => {
        if (!pendingPayload) return;

        try {
            setLoading(true);
            setError(null);

            if (gateway === 'finik') {
                const response = await fetch(`${API_BASE}/api/finik/ticket-checkout`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...pendingPayload, method })
                });

                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const data = await response.json();
                const paymentUrl = data?.paymentUrl;

                if (paymentUrl) {
                    setModalOpen(false);
                    setPendingPayload(null);
                    window.location.href = paymentUrl;
                    return;
                }
                throw new Error('paymentUrl not returned');
            }
        } catch (err) {
            setError(t('tickets.error'));
            console.error('Payment error:', err);
        } finally {
            setLoading(false);
        }
    };

    const inputBaseClass = `rounded-2xl px-4 py-3 w-full text-[15px] ${
        variant === 'hero' ? 'bg-white' : 'bg-white border border-gray-300'
    }`;
    const inputTextColor = (hasValue: boolean) => (hasValue ? 'text-black' : 'text-gray-600');

    const FormContent = (
        <>
            {/* Информация о событии */}
            {selectedEvent && (
                <div className="mb-6">
                    <h3 className={`text-xl font-medium mb-3 ${variant === 'hero' ? 'text-white' : 'text-gray-800'}`}>
                        {getEventName(selectedEvent)}
                    </h3>
                    <div className="space-y-1.5">
                        <div>
                            <span className={`text-lg ${variant === 'hero' ? 'text-lime-500' : 'text-green-600'}`}>{t('tickets.event_date')}</span>
                            <span className={`text-lg ${variant === 'hero' ? 'text-white' : 'text-gray-700'}`}> {formatEventDate(selectedEvent.eventDate)}</span>
                        </div>
                        <div>
                            <span className={`text-lg ${variant === 'hero' ? 'text-lime-500' : 'text-green-600'}`}>{t('tickets.location')}</span>
                            <span className={`text-lg ${variant === 'hero' ? 'text-white' : 'text-gray-700'}`}> {selectedEvent.locationName}</span>
                        </div>
                        <div>
                            <span className={`text-lg ${variant === 'hero' ? 'text-lime-500' : 'text-green-600'}`}>{t('tickets.ticket_price')}</span>
                            <span className={`text-lg ${variant === 'hero' ? 'text-white' : 'text-gray-700'}`}> {t('tickets.trees_per_ticket')}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Разделительная линия */}
            <div className={`w-full h-px mb-6 ${variant === 'hero' ? 'bg-white/20' : 'bg-gray-300'}`}></div>

            {/* Форма */}
            <form className="space-y-4" noValidate onSubmit={handleSubmit}>
                <div className={`text-lg ${variant === 'hero' ? 'text-white' : 'text-gray-800'}`}>
                    {t('tickets.your_data')}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    <input
                        className={`${inputBaseClass} ${inputTextColor(!!firstName)}`}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder={t('tickets.first_name')}
                        autoComplete="given-name"
                    />
                    <input
                        className={`${inputBaseClass} ${inputTextColor(!!lastName)}`}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder={t('tickets.last_name')}
                        autoComplete="family-name"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    <input
                        className={`${inputBaseClass} ${inputTextColor(!!(phone && phone !== '+'))}`}
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        maxLength={16}
                        value={phone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        onFocus={(e) => {
                            if (!e.currentTarget.value) setPhone('+');
                        }}
                        placeholder={t('tickets.phone')}
                    />
                    <div className="relative">
                        <input
                            className={`${inputBaseClass} ${inputTextColor(!!email)} ${
                                email.trim() && !isValidEmail(email) ? 'border-2 border-red-400' : ''
                            }`}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t('tickets.email')}
                            autoComplete="email"
                        />
                        {email.trim() && !isValidEmail(email) && (
                            <p className="text-red-500 text-xs mt-1 ml-2">
                                {t('tickets.email_invalid')}
                            </p>
                        )}
                    </div>
                </div>

                <div>
                    <input
                        className={`${inputBaseClass} ${inputTextColor(!!instagramUrl)}`}
                        type="text"
                        value={instagramUrl}
                        onChange={(e) => handleInstagramChange(e.target.value)}
                        placeholder={t('tickets.instagram')}
                        autoComplete="off"
                    />
                </div>

                {/* Разделительная линия */}
                <div className={`w-full h-px ${variant === 'hero' ? 'bg-white/20' : 'bg-gray-300'}`}></div>

                {/* Дополнительные участники */}
                <div className={`text-lg ${variant === 'hero' ? 'text-white' : 'text-gray-800'}`}>
                    {t('tickets.additional_participants')}
                </div>

                {/* Информация о билетах */}
                <div
                    className={`text-sm rounded-xl p-4 mb-3 leading-relaxed ${
                        variant === 'hero'
                            ? 'bg-white/10 text-white/95 border border-white/20'
                            : 'bg-green-50 text-green-800 border border-green-200'
                    }`}
                >
                    {t('tickets.children_note')}
                </div>

                {additionalParticipants.map((participant, index) => (
                    <div key={index} className="flex gap-2.5">
                        <input
                            className={`flex-1 rounded-2xl px-4 py-3 text-[15px] ${
                                variant === 'hero' ? 'bg-white' : 'bg-white border border-gray-300'
                            } ${inputTextColor(!!participant.fullName)}`}
                            value={participant.fullName}
                            onChange={(e) => updateParticipant(index, e.target.value)}
                            placeholder={t('tickets.participant_fullname')}
                        />
                        <button
                            type="button"
                            onClick={() => removeParticipant(index)}
                            className="px-3 py-3 bg-white border border-gray-300 rounded-2xl text-red-500 hover:bg-red-50 transition text-[15px]"
                        >
                            {t('tickets.remove')}
                        </button>
                    </div>
                ))}

                <button
                    type="button"
                    onClick={addParticipant}
                    className="px-3 py-3 bg-white border border-gray-300 rounded-2xl text-neutral-600 hover:bg-gray-50 transition text-[15px]"
                >
                    {t('tickets.add_more')}
                </button>

                {/* Разделительная линия */}
                <div className={`w-full h-px ${variant === 'hero' ? 'bg-white/20' : 'bg-gray-300'}`}></div>

                {error && <p className="text-red-600 text-sm">{error}</p>}

                {/* Итого */}
                <div
                    className={`rounded-xl p-4 ${
                        variant === 'hero'
                            ? 'bg-white/10 border border-white/20'
                            : 'bg-gray-50 border border-gray-200'
                    }`}
                >
                    <div className="flex justify-between items-center mb-2">
                        <span className={`text-base ${variant === 'hero' ? 'text-white/90' : 'text-gray-600'}`}>
                            {t('tickets.total_participants')}:
                        </span>
                        <span className={`text-lg font-medium ${variant === 'hero' ? 'text-white' : 'text-gray-900'}`}>
                            {participantsCount}
                        </span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className={`text-base ${variant === 'hero' ? 'text-white/90' : 'text-gray-600'}`}>
                            {t('tickets.total_amount')}:
                        </span>
                        <span className={`text-xl font-semibold ${variant === 'hero' ? 'text-lime-400' : 'text-green-600'}`}>
                            {amount.toLocaleString('ru-RU')} {t('donations.currency')}
                        </span>
                    </div>
                </div>

                <div className="pt-3">
                    <button
                        type="submit"
                        disabled={formInvalid || loading}
                        className={`py-4 px-6 rounded-full font-serif font-normal text-white transition inline-flex items-center justify-center gap-2 text-xl ${
                            formInvalid || loading
                                ? variant === 'hero' ? 'bg-[#cccccc] cursor-not-allowed' : 'bg-gray-300 cursor-not-allowed'
                                : variant === 'hero'
                                    ? 'bg-primary/80 backdrop-blur-[2px] hover:opacity-90'
                                    : 'bg-primary hover:bg-[#75C01D]'
                        }`}
                    >
                        {loading ? t('tickets.loading') : t('tickets.buy_ticket', { amount: amount.toLocaleString('ru-RU') })}
                    </button>
                </div>
            </form>

            {/* Модалка выбора способа */}
            <PaymentMethodModal
                open={modalOpen}
                amount={amount}
                loading={loading}
                onClose={() => {
                    setModalOpen(false);
                    setPendingPayload(null);
                }}
                onConfirm={handleConfirmGateway}
            />
        </>
    );

    return FormContent;
};

export default TicketForm;
